<!--
  Sección "¿Necesitas ayuda?". Usada por Home y por las páginas de Material de
  Apoyo (Bienvenida, Seguros, Biblioteca, Adjudicación, Pagos).

  Layout responsive sin props: apilado y centrado hasta 1024px; a partir de
  1025px pasa a horizontal con las tres tarjetas en fila y el texto alineado a
  la izquierda, según el Figma "Contacto multiples breakpoints web"
  (node 3957:17804). Los <br> del párrafo son para el apilado móvil y se ocultan
  en desktop mediante .ds-contact-help__break.
-->
<template>
  <div class="ds-contact-help">
    <span class="ds-contact-help__chip">
      <img :src="callCenterIcon" alt="" class="ds-contact-help__chip-icon" aria-hidden="true" />
      <span class="ds-contact-help__chip-copy">Contacto</span>
    </span>

    <div class="ds-contact-help__copy">
      <DsText as="subtitle4" class="ds-contact-help__title">&iquest;Necesitas ayuda?</DsText>
      <DsText as="subtitle4" class="ds-contact-help__highlight">Estamos para ti</DsText>
      <DsText as="body4" class="ds-contact-help__intro">
        Nuestro equipo est&aacute; listo para acompa&ntilde;arte<span class="ds-contact-help__break"><br /></span> en
        todo momento<span class="ds-contact-help__break"><br /></span>&#32;<strong
          >de Lunes a viernes de 9:00 a 18:00</strong
        >
      </DsText>
    </div>

    <div class="ds-contact-help__contacts">
      <PaymentsClosingContactCard
        v-for="contact in contacts"
        :key="contact.title"
        :icon="contact.icon"
        :title="contact.title"
        :detail="contact.detail"
        :href="contact.href"
        :external="contact.external"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import DsText from '../atoms/DsText.vue'
import PaymentsClosingContactCard from './DsContactCard.vue'

const addCallIcon = new URL('../../../../../assets/add_call.svg', import.meta.url).href
const callCenterIcon = new URL('../../../../../assets/callcenter.svg', import.meta.url).href
const emailIcon = new URL('../../../../../assets/emailicon.svg', import.meta.url).href
const whatsappIcon = new URL('../../../../../assets/whatsapp.svg', import.meta.url).href

const contacts = [
  {
    icon: addCallIcon,
    title: 'Ll\u00e1manos',
    detail: '55 5511 9910 o al 800 447 2272',
    href: 'tel:+525555119910'
  },
  {
    icon: whatsappIcon,
    title: 'WhatsApp',
    detail: '55 7665 9885',
    href: 'http://wa.me/5215576659885',
    external: true
  },
  {
    icon: emailIcon,
    title: 'Escr\u00edbenos',
    detail: 'atencionaclientes@hircasa.com.mx',
    href: 'mailto:atencionaclientes@hircasa.com.mx'
  }
]
</script>

<style scoped>
.ds-contact-help {
  width: 100vw;
  background: var(--ds-purple-50);
  border-radius: 0;
  padding: 40px 20px;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  box-sizing: border-box;
}

.ds-contact-help__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 999px;
  background: var(--ds-grey-50);
  margin-left: 50%;
  transform: translateX(-50%);
}

.ds-contact-help__chip-icon {
  width: 16px;
  height: 16px;
  display: block;
}

.ds-contact-help__chip-copy {
  color: var(--ds-purple-main);
  font-size: var(--ds-body4-size);
  font-weight: 500;
  font-family: var(--ds-font-family-inter);
  line-height: 1;
}

.ds-contact-help__copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-top: 32px;
  text-align: center;
}

.ds-contact-help__title,
.ds-contact-help__highlight {
  color: var(--ds-purple-900);
  font-size: var(--ds-h4-size);
  font-weight: 700;
  font-family: var(--ds-font-family-bebas);
  line-height: 1;
}

.ds-contact-help__highlight {
  color: var(--ds-purple-400);
}

.ds-contact-help__intro {
  max-width: 44ch;
  color: var(--ds-text-primary);
  font-size: var(--ds-body4-size);
  font-weight: 400;
  font-family: var(--ds-font-family-inter);
  line-height: 1.2;
  margin-top: 8px;
}

.ds-contact-help__intro :deep(strong) {
  font-weight: 700;
}

.ds-contact-help__contacts {
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-top: 40px;
  max-width: 350px;
  margin: 0 auto;
}

.ds-contact-help__contacts :deep(.closing-contact-card) {
  min-height: 72px;
  padding: 16px 8px;
  gap: 16px;
  border-radius: 4px;
}

.ds-contact-help__contacts :deep(.closing-contact-card__icon-wrap) {
  width: 48px;
  height: 48px;
}

.ds-contact-help__contacts :deep(.closing-contact-card__icon) {
  width: 20px;
  height: 20px;
}

.ds-contact-help__contacts :deep(.closing-contact-card__copy) {
  gap: 8px;
}

.ds-contact-help__contacts :deep(.closing-contact-card__title) {
  font-size: var(--ds-body3-size);
  font-weight: 600;
}

.ds-contact-help__contacts :deep(.closing-contact-card__link) {
  font-size: var(--ds-body4-size);
}

@media (min-width: 768px) {
  .ds-contact-help {
    padding: 40px 24px 48px;
  }

  .ds-contact-help__chip {
    padding: 6px 16px;
  }

  .ds-contact-help__copy {
    padding-top: 28px;
  }

  .ds-contact-help__intro {
    max-width: 38ch;
  }

  .ds-contact-help__contacts {
    gap: 24px;
    padding-top: 32px;
    max-width: 350px;
  }
}

@media (min-width: 1208px) {
  .ds-contact-help {
    padding: 40px 32px 48px;
  }

  .ds-contact-help__chip {
    padding: 6px 16px;
  }

  .ds-contact-help__copy {
    padding-top: 28px;
  }

  .ds-contact-help__intro {
    max-width: 38ch;
  }

  .ds-contact-help__contacts {
    gap: 28px;
    padding-top: 40px;
    max-width: 350px;
  }
}

/*
  Layout horizontal en desktop (Figma "Contacto multiples breakpoints web",
  node 3957:17804): contenido alineado a la izquierda y las tres tarjetas en
  fila. Aplica a TODAS las páginas que usan el componente; por debajo de
  1025px se mantiene el apilado vertical centrado definido arriba.
*/
@media (min-width: 1025px) {
  .ds-contact-help {
    padding: 32px 140px;
  }

  .ds-contact-help__chip {
    margin-left: 0;
    transform: none;
  }

  .ds-contact-help__copy {
    align-items: flex-start;
    text-align: left;
    padding-top: 32px;
  }

  /*
    Título en una sola línea con dos colores (Figma H1/H1-bold 32px).
    El contenedor es flex column, así que los dos DsText se envuelven en una
    línea propia mediante `display: contents` sobre el wrapper de texto.
  */
  .ds-contact-help__copy {
    display: block;
  }

  .ds-contact-help__title,
  .ds-contact-help__highlight {
    display: inline;
    font-size: var(--ds-h4-size);
  }

  .ds-contact-help__title::after {
    content: ' ';
    white-space: pre;
  }

  /*
    En desktop el párrafo va en una sola línea (Figma: 1160px de ancho).
    Los <br> del template son para el apilado móvil, así que se anulan.
  */
  .ds-contact-help__intro {
    max-width: none;
    font-size: var(--ds-body3-size);
    line-height: 24px;
    /* .ds-contact-help__copy deja de ser flex: el gap se repone aquí. */
    margin-top: 16px;
  }

  /* Los saltos del apilado móvil se ocultan; el texto fluye en una línea. */
  .ds-contact-help__intro :deep(.ds-contact-help__break) {
    display: none;
  }

  .ds-contact-help__contacts {
    flex-direction: row;
    gap: 24px;
    padding-top: 40px;
    max-width: none;
    margin: 0;
  }

  .ds-contact-help__contacts :deep(.closing-contact-card) {
    flex: 1;
    min-width: 0;
  }
}
</style>
